// AM Advertising Home — the filter row's narrowing. PURE + tested.
//
// Scope picks the BOOK (server-side, by AM); the filters narrow WITHIN it,
// client-side, off the account dimensions the overview already carries. ONE
// filtered account set + ONE row filter feed every section — verdict, alerts,
// pulse, book table, wins & worries, channel comparison, creatives — which is
// what makes "the whole page reflects the filter" structural rather than
// per-section discipline.

import type { HomeAccount } from '../../types/advertisingHome.js';
import type { ReportingAccountFacet } from '../../types/reporting.js';

export interface HomeFilterSelection {
  brandAccountIds: string[];
  groupAccountIds: string[];
  countries: string[];
  accountIds: string[];
  /**
   * ⚠️⚠️ A CLIENT-SIDE FILTER OVER THE LOADED BOOK, NOT A SCOPE CHANGE.
   * The AM control on Advertising Home is the SCOPE switcher, which re-fetches
   * and re-scopes the alert evaluation; the specialist boards are fixed to the
   * whole roster and want to narrow WITHIN it, which is a different question
   * and must not move the server-side scope. Contains uids, plus possibly the
   * UNASSIGNED_AM_ID sentinel.
   */
  accountManagerIds: string[];
}

/**
 * ⚠️⚠️ UNASSIGNED IS A SELECTABLE VALUE, NOT A DROPPED ROW. An account
 * with no manager is exactly the one nobody is watching, so it has to be
 * reachable — and the reporting faceting cannot supply it (`availableOptions`
 * drops null ids by design), which is why the AM options are built here rather
 * than through that helper.
 *
 * A sentinel rather than an empty string: an empty selection already means "no
 * constraint" everywhere in this module, so "" would be unrepresentable.
 */
export const UNASSIGNED_AM_ID = '__unassigned__';
export const UNASSIGNED_AM_LABEL = 'Unassigned';
/** A uid that resolves to nobody — a departed manager. Never the raw uid. */
export const UNKNOWN_AM_LABEL = 'Unknown manager';

export const EMPTY_HOME_FILTER: HomeFilterSelection = {
  brandAccountIds: [],
  groupAccountIds: [],
  countries: [],
  accountIds: [],
  accountManagerIds: [],
};

export function hasActiveHomeFilter(sel: HomeFilterSelection): boolean {
  return (
    sel.brandAccountIds.length > 0 ||
    sel.groupAccountIds.length > 0 ||
    sel.countries.length > 0 ||
    sel.accountIds.length > 0 ||
    sel.accountManagerIds.length > 0
  );
}

const passes = (selected: string[], value: string | null): boolean => {
  if (selected.length === 0) return true; // no constraint
  return value !== null && selected.includes(value); // null never matches a non-empty selection
};

/**
 * The AM filter's own matcher. ⚠️ A null manager matches ONLY when the
 * sentinel was picked — `passes` would drop those accounts entirely, which is
 * the behaviour this filter exists to avoid.
 */
const passesAm = (selected: string[], value: string | null): boolean => {
  if (selected.length === 0) return true; // no constraint
  if (value === null) return selected.includes(UNASSIGNED_AM_ID);
  return selected.includes(value);
};

/** The accounts surviving the filter row (AND across filters, OR within one). */
export function filterBookAccounts(
  accounts: readonly HomeAccount[],
  sel: HomeFilterSelection,
): HomeAccount[] {
  return accounts.filter(
    (a) =>
      passes(sel.brandAccountIds, a.brandAccountId) &&
      passes(sel.groupAccountIds, a.groupAccountId) &&
      passes(sel.countries, a.country) &&
      passes(sel.accountIds, a.id) &&
      passesAm(sel.accountManagerIds, a.accountManagerId),
  );
}

/**
 * The AM filter's options for a book: every manager who owns an account in it,
 * plus "Unassigned" when any account has none.
 *
 * ⚠️ The Unassigned entry sorts LAST rather than alphabetically — it is not
 * a person, and filing it under U beside real names reads as one.
 * ⚠️ A uid `amNames` cannot resolve renders as "Unknown manager", NEVER as the
 * raw uid: the same rule the specialist worklists already follow.
 */
export function amFilterOptions(
  accounts: readonly HomeAccount[],
  amNames: ReadonlyMap<string, string>,
): Array<{ id: string; name: string }> {
  const byId = new Map<string, string>();
  let anyUnassigned = false;
  for (const a of accounts) {
    if (!a.accountManagerId) {
      anyUnassigned = true;
      continue;
    }
    if (!byId.has(a.accountManagerId)) {
      byId.set(a.accountManagerId, amNames.get(a.accountManagerId) ?? UNKNOWN_AM_LABEL);
    }
  }
  const named = [...byId.entries()]
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
  return anyUnassigned
    ? [...named, { id: UNASSIGNED_AM_ID, name: UNASSIGNED_AM_LABEL }]
    : named;
}

/** Narrow any account-keyed rows (spine days, creatives) to the filtered set. */
export function filterRowsToAccounts<T extends { accountId: string }>(
  rows: readonly T[],
  accounts: readonly HomeAccount[],
): T[] {
  const ids = new Set(accounts.map((a) => a.id));
  return rows.filter((r) => ids.has(r.accountId));
}

/**
 * The book as facet rows, so the filter bar reuses the reporting dashboards'
 * exclude-self faceting (src/lib/reportingFacets.availableOptions) VERBATIM —
 * same components, same cascade, same "null never matches" rule. Industry + AM
 * aren't filters on this page, so those columns ride as nulls.
 */
export function accountFacetsFromBook(accounts: readonly HomeAccount[]): ReportingAccountFacet[] {
  return accounts.map((a) => ({
    accountId: a.id,
    accountName: a.name,
    brandAccountId: a.brandAccountId,
    brandName: a.brandName,
    groupAccountId: a.groupAccountId,
    groupName: a.groupName,
    industry: null,
    accountManagerId: a.accountManagerId,
    accountManagerName: null,
    country: a.country,
  }));
}
